
import { useAuth } from '@/hooks/useAuth';
import { collection, doc, addDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { Loader2, Palette, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { getCalendarioHermandadColors, setCalendarioHermandadColors } from '@/lib/actions';
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { CalendarEvent } from '@/lib/types';
import { Calendar } from '@/components/jartinet/calendario/Calendar';
import { useToast } from '@/hooks/use-toast';
import { sanitizeFirestoreData } from '@/lib/utils';


/**
 * Configurador del color de evento de cada hermandad. Se guarda en
 * `app_settings/calendario` (lectura pública, escritura admin) y lo consume
 * el calendario para pintar los eventos que tengan hermandad asignada.
 */
function HermandadColorsDialog() {
  const { jartinetData } = useAuth();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [colors, setColors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [term, setTerm] = useState('');

  useEffect(() => { if (open) getCalendarioHermandadColors().then(setColors); }, [open]);

  const hermandades = useMemo(() => {
    const all = [...(jartinetData?.hermandades || [])].sort((a, b) => a.nombre.localeCompare(b.nombre));
    const q = term.trim().toLowerCase();
    return q ? all.filter(h => h.nombre.toLowerCase().includes(q)) : all;
  }, [jartinetData?.hermandades, term]);

  const save = async () => {
    setSaving(true);
    const res = await setCalendarioHermandadColors(colors);
    setSaving(false);
    if (res.success) { toast({ title: 'Guardado', description: res.message }); setOpen(false); }
    else toast({ title: 'Error', description: res.message, variant: 'destructive' });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2 rounded-xl">
          <Palette className="h-4 w-4" /> Colores de hermandades
        </Button>
      </DialogTrigger>
      <DialogContent className="flex max-h-[85dvh] flex-col p-0 sm:max-w-lg">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>Color de evento por hermandad</DialogTitle>
          <DialogDescription>
            Los eventos con hermandad asignada se pintan con este color. Por defecto se usa el
            color de la propia hermandad; cámbialo aquí solo si quieres otro distinto.
          </DialogDescription>
        </DialogHeader>
        <div className="px-6 pb-2">
          <Input value={term} onChange={e => setTerm(e.target.value)} placeholder="Buscar hermandad..." />
        </div>
        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-6 pb-4">
          {hermandades.map(h => {
            const value = colors[h.id] || '';
            // Sin color propio configurado, se usa (y se muestra) su colorPrimario.
            const efectivo = value || h.colorPrimario || '#b4841c';
            return (
              <div key={h.id} className="flex items-center gap-3 rounded-xl border border-white/8 bg-muted/20 p-2">
                {h.escudoUrl && <img src={h.escudoUrl} alt="" className="h-8 w-8 shrink-0 object-contain" />}
                <span className="min-w-0 flex-1 truncate text-sm">
                  {h.nombre}
                  {!value && <span className="block text-[0.65rem] text-muted-foreground">Color de la hermandad</span>}
                </span>
                <Input
                  type="color"
                  value={/^#[0-9a-fA-F]{6}$/.test(efectivo) ? efectivo : '#b4841c'}
                  onChange={e => setColors(p => ({ ...p, [h.id]: e.target.value }))}
                  className="h-9 w-12 shrink-0 p-1"
                />
                <Input
                  value={value.toUpperCase()}
                  onChange={e => {
                    const v = e.target.value.trim();
                    setColors(p => ({ ...p, [h.id]: (v.startsWith('#') ? v : `#${v}`).slice(0, 7).toLowerCase() }));
                  }}
                  placeholder={(h.colorPrimario || '#B4841C').toUpperCase()}
                  maxLength={7}
                  className="h-9 w-28 shrink-0 font-mono text-xs uppercase"
                />
                {value && (
                  <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0"
                    onClick={() => setColors(p => { const n = { ...p }; delete n[h.id]; return n; })}>
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            );
          })}
          {hermandades.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">No hay hermandades.</p>
          )}
        </div>
        <DialogFooter className="border-t border-white/8 px-6 py-4">
          <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancelar</Button>
          <Button onClick={save} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Guardar colores
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminCalendarioPage() {
  const { user, userData, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [isEventsLoading, setIsEventsLoading] = useState(true);

  useEffect(() => {
    if (!loading && userData?.role !== 'admin') {
      navigate('/jartinet');
    }
  }, [user, userData, loading, navigate]);
  
  useEffect(() => {
    const eventsQuery = collection(db, 'events');
    const unsubscribe = onSnapshot(eventsQuery, (snapshot) => {
      const formattedEvents = snapshot.docs.map(doc => {
        const data = sanitizeFirestoreData(doc.data());
        return {
          ...data,
          id: doc.id,
          start: new Date(data.start),
          end: new Date(data.end),
        } as CalendarEvent;
      });
      setEvents(formattedEvents);
      setIsEventsLoading(false);
    }, (error) => {
      console.error("Error fetching events:", error);
      toast({ title: 'Error', description: 'No se pudieron cargar los eventos.', variant: 'destructive'});
      setIsEventsLoading(false);
    });

    return () => unsubscribe();
  }, [toast]);
  
  const handleApiAction = async (method: 'POST' | 'PUT' | 'DELETE', body?: any) => {
    try {
        if(method === 'POST') {
            await addDoc(collection(db, 'events'), body);
        } else if (method === 'PUT' && body.id) {
            const { id, ...dataToUpdate } = body;
            await updateDoc(doc(db, 'events', id), dataToUpdate);
        } else if (method === 'DELETE' && body.id) {
            await deleteDoc(doc(db, 'events', body.id));
        }
        toast({ title: 'Éxito', description: `Evento ${method === 'POST' ? 'creado' : method === 'PUT' ? 'actualizado' : 'eliminado'}.` });
        return { success: true };
    } catch(e) {
        console.error(e);
        toast({ title: 'Error', description: 'No se pudo completar la acción.', variant: 'destructive' });
        return { success: false };
    }
  };

  const handleCreateEvent = (eventData: Omit<CalendarEvent, 'id'>) => {
    return handleApiAction('POST', eventData);
  };

  const handleUpdateEvent = (eventId: string, eventData: Partial<CalendarEvent>) => {
    return handleApiAction('PUT', { id: eventId, ...eventData });
  };

  const handleDeleteEvent = (eventId: string) => {
    return handleApiAction('DELETE', { id: eventId });
  };


  if (loading || userData?.role !== 'admin') {
    return (
      <div className="flex h-[calc(100vh-10rem)] w-full items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="ml-4">Verificando permisos...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 pb-8">
       <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-headline text-3xl font-bold text-primary">
            Gestionar Calendario
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Haz clic en un día vacío para crear un evento, o en uno con eventos para verlos y editarlos.
          </p>
        </div>
        <HermandadColorsDialog />
      </div>
      <div>
          <Calendar
            events={events}
            isLoading={isEventsLoading}
            isEditable={true}
            onCreateEvent={handleCreateEvent}
            onUpdateEvent={handleUpdateEvent}
            onDeleteEvent={handleDeleteEvent}
          />
      </div>
    </div>
  );
}
